import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const RETURN_PARAM = 'kyc_idv';

interface KycIdvDialogState<T> {
  /** The subject whose dialog is open, e.g. a legal entity id; `null` when closed. */
  openFor: T | null;
  openDialog: (subject: T) => void;
  closeDialog: () => void;
  /** Where MangoPay sends the user back to; reopens the dialog for the subject. */
  returnUrlFor: (subject: T) => string;
}

/** KYC dialog open state; a return URL param reopens it after MangoPay. `returnPath` replaces the current page in it. */
export const useKycIdvDialogState = <T extends string>(
  returnPath?: string
): KycIdvDialogState<T> => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [openedFor, setOpenedFor] = useState<T | null>(null);

  const returnedFor = searchParams.get(RETURN_PARAM) as T | null;

  const closeDialog = useCallback(() => {
    setOpenedFor(null);
    if (!returnedFor) {
      return;
    }
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.delete(RETURN_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [returnedFor, setSearchParams]);

  const returnUrlFor = useCallback(
    (subject: T) => {
      const url = returnPath
        ? new URL(returnPath, window.location.origin)
        : new URL(window.location.href);
      url.searchParams.set(RETURN_PARAM, subject);
      return url.toString();
    },
    [returnPath]
  );

  return {
    openFor: openedFor ?? returnedFor,
    openDialog: setOpenedFor,
    closeDialog,
    returnUrlFor,
  };
};
